import type { NavigationGuardNext } from 'vue-router'
import type { UserRoleId } from '@/types/authorisation/role-id.enum'
import NProgress from 'accessible-nprogress'
import { createRouter, createWebHistory } from 'vue-router'
import { useIsPartnerWithoutOrganisations } from '@/composables/useIsPartnerWithoutOrganisations'
import { useUserStore } from '@/stores/user.store'
import { authorizeFactory } from '@/utils/authorization/authorization-factory'
import { asideCollapsibleHook } from './hooks/asideCollapsible.hook'
import { authorizeHook } from './hooks/authorize.hook'
import { breadcrumbHook } from './hooks/breadcrumb.hook'
import { setupHook } from './hooks/setup.hook'
import { validateResourceHook } from './hooks/validateResource.hook'

declare module 'vue-router' {
  interface RouteMeta {
    setup?: {
      organisation?: boolean
      user?: boolean
    }
    authorize?: {
      allowedMinimumRole?: UserRoleId
      disallowedRedirectTo?: string
    }
    asideCollapsable?: boolean
    breadcrumb?: { shouldClear?: boolean, visible?: boolean }
    buttonLabel?: string
    excludeFromNav?: boolean
    requiresActiveSite?: boolean
    title?: string
  }
}

NProgress.configure({ showSpinner: false })

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/',
      name: 'app',
      component: () => import('@/layouts/AppLayout.vue'),
      redirect: {
        name: 'app-dashboard',
      },
      meta: {
        authorize: authorizeFactory().getOrganisationUser(),
        setup: {
          user: true,
        },
      },
      children: [
        {
          path: '/home',
          redirect: {
            name: 'app-dashboard',
          },
        },
        // '/dashboard'
        {
          path: '/dashboard',
          name: 'app-dashboard',
          component: () => import('@/views/app/DashboardView.vue'),
          meta: {
            breadcrumb: { visible: false },
            title: 'pages.titles.dashboard',
          },
          beforeEnter: () => {
            if (useIsPartnerWithoutOrganisations.value) {
              return { name: 'app-settings-partner-organisations' }
            }

            return true
          },
        },
        // '/chatbot'
        {
          path: '/chatbot',
          name: 'app-chatbot',
          components: {
            default: () => import('@/views/app/ChatbotView.vue'),
            Aside: () => import('@/components/chatbot/ChatbotSessions.vue'),
          },
          meta: {
            asideCollapsable: true,
            breadcrumb: { visible: false },
            title: 'pages.titles.chatbot',
          },
          beforeEnter: () => {
            if (!useUserStore().chatbotEnabled) {
              return { name: 'app-dashboard' }
            }

            if (useIsPartnerWithoutOrganisations.value) {
              return { name: 'app-settings-partner-organisations' }
            }

            return true
          },
        },
        // '/alerts'
        {
          path: '/alerts',
          name: 'app-alerts',
          components: {
            default: () => import('@/views/app/AlertsView.vue'),
            Aside: () => import('@/components/alert/AlertsSettings.vue'),
            Nav: () => import('@/components/alert/AlertsNav.vue'),
          },
          beforeEnter: () => {
            if (useIsPartnerWithoutOrganisations.value) {
              return { name: 'app-settings-partner-organisations' }
            }

            return true
          },
          redirect: {
            name: 'app-alerts-dashboard',
          },
          children: [
            {
              path: 'dashboard',
              name: 'app-alerts-dashboard',
              component: () => import('@/components/alert/AlertsDisplay.vue'),
              meta: {
                asideCollapsable: true,
                breadcrumb: { visible: false },
                title: 'pages.titles.alerts.dashboard',
              },
            },
            {
              path: 'archive',
              name: 'app-alerts-archive',
              component: () => import('@/components/alert/AlertsDisplay.vue'),
              meta: {
                asideCollapsable: true,
                authorize: authorizeFactory().getOrganisationUser(),
                breadcrumb: { visible: false },
                setup: {
                  user: true,
                },
                title: 'pages.titles.alerts.archive',
              },
            },
          ],
        },
        // '/analytics'
        {
          path: '/analytics',
          name: 'app-analytics',
          components: {
            default: () => import('@/views/app/AnalyticsView.vue'),
            Aside: () => import('@/components/analytics/AnalyticsSettings.vue'),
          },
          beforeEnter: () => {
            if (useIsPartnerWithoutOrganisations.value) {
              return { name: 'app-settings-partner-organisations' }
            }

            return true
          },
          redirect: {
            name: 'app-analytics-display',
          },
          children: [
            {
              path: 'site/:siteId?/display',
              name: 'app-analytics-display',
              component: () => import('@/components/analytics/AnalyticsDisplay.vue'),
              meta: {
                asideCollapsable: true,
                authorize: authorizeFactory().getOrganisationUser(),
                breadcrumb: { visible: false },
                setup: {
                  user: true,
                },
                title: 'pages.titles.analytics.analytics',
              },
              props: true,
            },
          ],
        },
        // '/reports'
        {
          path: '/reports',
          name: 'app-reports',
          components: {
            default: () => import('@/views/app/ReportsView.vue'),
            Aside: () => import('@/components/reports/ReportsSettings.vue'),
          },
          beforeEnter: () => {
            if (useIsPartnerWithoutOrganisations.value) {
              return { name: 'app-settings-partner-organisations' }
            }

            return true
          },
          redirect: {
            name: 'app-reports-display',
          },
          children: [
            {
              path: 'site/:siteId?/display',
              name: 'app-reports-display',
              component: () => import('@/components/reports/ReportsDisplay.vue'),
              meta: {
                asideCollapsable: true,
                authorize: authorizeFactory().getOrganisationUser(),
                breadcrumb: { visible: false },
                setup: {
                  user: true,
                },
                title: 'pages.titles.reports.energyPerformance',
              },
              props: true,
            },
            {
              path: 'energy-certificate/:siteId?',
              name: 'app-reports-energy-certificate',
              component: () => import('@/components/reports/ReportsEnergyCertificate.vue'),
              meta: {
                asideCollapsable: true,
                authorize: authorizeFactory().getOrganisationUser(),
                breadcrumb: { visible: false },
                setup: {
                  user: true,
                },
                title: 'pages.titles.reports.energyCertificate',
              },
              props: true,
            },
            {
              path: 'nerc-tariff-compliance/:siteId?',
              name: 'app-reports-nerc-tariff-compliance',
              component: () => import('@/components/reports/ReportsNercTariffCompliance.vue'),
              meta: {
                asideCollapsable: true,
                authorize: authorizeFactory().getOrganisationUser(),
                breadcrumb: { visible: false },
                setup: {
                  user: true,
                },
                title: 'pages.titles.reports.nercTariffCompliance',
              },
              props: true,
            },
            {
              path: 'exports',
              name: 'app-reports-exports',
              component: () => import('@/components/reports/ReportsExports.vue'),
              meta: {
                asideCollapsable: true,
                authorize: authorizeFactory().getOrganisationUser(),
                breadcrumb: { visible: false },
                setup: {
                  user: true,
                },
                title: 'pages.titles.reports.exports',
              },
              props: true,
            },
          ],
        },
        // '/sizing'
        {
          path: '/sizing',
          name: 'app-sizing',
          components: {
            default: () => import('@/views/app/SizingView.vue'),
            Aside: () => import('@/components/sizing/SizingSettings.vue'),
          },
          beforeEnter: () => {
            if (useIsPartnerWithoutOrganisations.value) {
              return { name: 'app-settings-partner-organisations' }
            }

            return true
          },
          redirect: {
            name: 'app-sizing-display',
          },
          children: [
            {
              path: 'site/:siteId?/display',
              name: 'app-sizing-display',
              component: () => import('@/components/sizing/SizingDisplay.vue'),
              meta: {
                asideCollapsable: true,
                authorize: authorizeFactory().getOrganisationUser(),
                breadcrumb: { visible: false },
                setup: {
                  user: true,
                },
                title: 'pages.titles.sizing.display',
              },
              props: true,
            },
          ],
        },
        // '/migration'
        {
          path: '/migration',
          name: 'app-migration',
          component: () => import('@/views/app/Migration/Migration.vue'),
          meta: {
            authorize: authorizeFactory().getPartnerAdmin(),
            breadcrumb: { visible: false },
            title: 'Inverter Migration',
            setup: {
              user: true,
            },
          },
        },
        // '/support'
        {
          path: '/support',
          name: 'app-support',
          component: () => import('@/views/app/support/SupportView.vue'),
          meta: {
            authorize: authorizeFactory().getSiteUser(),
            breadcrumb: { visible: false },
            title: 'pages.titles.support.dashboard',
          },
        },
        // '/support/ticket
        {
          path: '/support/ticket/:ticketId?',
          name: 'app-support-ticket',
          component: () => import('@/views/app/support/TicketView.vue'),
          meta: {
            authorize: authorizeFactory().getSiteUser(),
            breadcrumb: { visible: false },
            title: 'pages.titles.support.ticket',
          },
          props: true,
        },
        // '/settings'
        {
          path: '/settings',
          name: 'app-settings',
          components: {
            default: () => import('@/views/app/setting/SettingsView.vue'),
            Aside: () => import('@/components/setting/SettingAside.vue'),
          },
          meta: {
            breadcrumb: { visible: false },
            setup: {
              user: true,
            },
          },
          redirect: {
            name: 'app-settings-personal-profile',
          },
          children: [
            // Personal
            {
              path: 'personal/profile',
              name: 'app-settings-personal-profile',
              component: () => import('@/views/app/setting/personal/ProfileView.vue'),
              meta: {
                authorize: authorizeFactory().getSiteUser(),
                title: 'pages.titles.settings.personal.profile',
              },
            },
            {
              path: 'personal/language-region',
              name: 'app-settings-personal-language-region',
              component: () => import('@/views/app/setting/personal/LanguageRegionView.vue'),
              meta: {
                authorize: authorizeFactory().getSiteUser(),
                title: 'pages.titles.settings.personal.languageRegion',
              },
            },
            {
              path: 'personal/notifications',
              name: 'app-settings-personal-notifications',
              component: () => import('@/views/app/setting/personal/NotificationPreferencesView.vue'),
              meta: {
                authorize: authorizeFactory().getSiteUser(),
                title: 'pages.titles.settings.personal.notifications',
              },
            },
            // Organisation
            {
              path: 'organisation/general',
              name: 'app-settings-organisation-general',
              component: () => import('@/views/app/setting/organisation/GeneralView.vue'),
              meta: {
                authorize: authorizeFactory().getOrganisationAdmin(),
                title: 'pages.titles.settings.organisation.general',
              },
            },
            {
              path: 'organisation/users',
              name: 'app-settings-organisation-users',
              component: () => import('@/views/app/setting/organisation/UsersView.vue'),
              meta: {
                authorize: authorizeFactory().getOrganisationAdmin(),
                title: 'pages.titles.settings.organisation.users',
              },
            },
            {
              path: 'organisation/connection',
              name: 'app-settings-organisation-connection',
              component: () => import('@/views/app/setting/organisation/ConnectionView.vue'),
              meta: {
                authorize: authorizeFactory().getOrganisationAdmin(),
                title: 'pages.titles.settings.organisation.connection',
              },
            },
            {
              path: 'organisation/alert-definitions',
              name: 'app-settings-organisation-alert-definitions',
              component: () => import('@/views/app/setting/organisation/AlertDefinitionsView.vue'),
              meta: {
                authorize: authorizeFactory().getOrganisationAdmin(),
                title: 'pages.titles.settings.organisation.alerts',
              },
            },
            {
              path: 'organisation/application',
              name: 'app-settings-organisation-application-settings',
              component: () => import('@/views/app/setting/organisation/ApplicationSettingsView.vue'),
              meta: {
                authorize: authorizeFactory().getOrganisationAdmin(),
                title: 'pages.titles.settings.organisation.application',
              },
            },
            {
              path: 'organisation/integration',
              name: 'app-settings-organisation-integration',
              component: () => import('@/views/app/setting/organisation/IntegrationView.vue'),
              meta: {
                authorize: authorizeFactory().getOrganisationAdmin(),
                title: 'pages.titles.settings.organisation.integration',
              },
            },
            // Exports
            {
              path: 'exports/site/:siteId?/battery-bank/:batteryBankId?',
              name: 'app-settings-exports-battery-bank',
              component: () => import('@/views/app/setting/exports/BatteryBankExportView.vue'),
              meta: {
                authorize: authorizeFactory().getOrganisationAdmin(),
                title: 'pages.titles.settings.exports.batteryBank',
              },
            },
            // Partner
            {
              path: 'partner/organisations',
              name: 'app-settings-partner-organisations',
              component: () => import('@/views/app/setting/partner/OrganisationsView.vue'),
              meta: {
                authorize: authorizeFactory().getPartnerAdmin(),
                title: 'pages.titles.settings.partner.organisations',
              },
            },
            {
              path: 'partner/users',
              name: 'app-settings-partner-users',
              component: () => import('@/views/app/setting/partner/UsersView.vue'),
              meta: {
                authorize: authorizeFactory().getPartnerAdmin(),
                title: 'pages.titles.settings.partner.users',
              },
            },
          ],
        },
        // '/site'
        {
          path: '/site/:siteId',
          name: 'app-site',
          components: {
            default: () => import('@/views/app/site/SiteView.vue'),
            Nav: () => import('@/components/site/SiteNav.vue'),
            Aside: () => import('@/components/site/SiteAside.vue'),
          },
          // Set shared meta settings for all child routes of '/site'
          meta: {
            asideCollapsable: true,
            breadcrumb: { shouldClear: false },
            requiresActiveSite: true,
          },
          props: true,
          redirect: {
            name: 'app-site-overview',
          },
          children: [
            {
              path: 'overview',
              name: 'app-site-overview',
              component: () => import('@/views/app/site/OverviewView.vue'),
              meta: {
                title: 'pages.titles.site.overview',
              },
            },
            {
              path: 'alerts',
              name: 'app-site-alerts',
              component: () => import('@/views/app/site/AlertsView.vue'),
              meta: {
                title: 'pages.titles.site.alerts',
              },
            },
            {
              path: 'dsm-management',
              name: 'app-site-dsm-management',
              component: () => import('@/views/app/site/DsmManagementView.vue'),
              meta: {
                title: 'pages.titles.site.dsmManagement',
              },
            },
            {
              path: 'dsm-runs',
              name: 'app-site-dsm-runs',
              component: () => import('@/views/app/site/SiteDsmRunsView.vue'),
              meta: {
                title: 'pages.titles.site.dsmRuns',
              },
            },
            {
              path: 'configuration',
              name: 'app-site-configuration',
              component: () => import('@/views/app/site/ConfigurationView.vue'),
              redirect: {
                name: 'app-site-configuration-details',
              },
              children: [
                {
                  path: 'details',
                  name: 'app-site-configuration-details',
                  component: () => import('@/views/app/site/configuration/DetailsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.details',
                    title: 'pages.titles.site.configuration.details',
                  },
                },
                {
                  path: 'data-gateways',
                  name: 'app-site-configuration-data-gateways',
                  component: () => import('@/views/app/site/configuration/MonitorsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.dataGateways',
                    title: 'pages.titles.site.configuration.monitors',
                  },
                },
                {
                  path: 'changeover-switches',
                  name: 'app-site-configuration-changeover-switches',
                  component: () => import('@/views/app/site/configuration/ChangeoverSwitchesView.vue'),
                  meta: {
                    buttonLabel: 'buttons.changeoverSwitches',
                    title: 'pages.titles.site.configuration.changeoverSwitches',
                  },
                },
                {
                  path: 'inverters',
                  name: 'app-site-configuration-inverters',
                  component: () => import('@/views/app/site/configuration/InvertersView.vue'),
                  meta: {
                    buttonLabel: 'inverters.plural',
                    title: 'pages.titles.site.configuration.inverters',
                  },
                },
                {
                  path: 'resource-groups',
                  name: 'app-site-configuration-resource-groups',
                  component: () => import('@/views/app/site/configuration/AssetFamilyGroupsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.assetFamilyGroups',
                    title: 'pages.titles.site.configuration.assetFamilyGroups',
                  },
                },
                {
                  path: 'resource-groups/details/:assetFamilyGroupId',
                  name: 'app-site-configuration-resource-group-details',
                  component: () => import('@/views/app/site/configuration/AssetFamilyGroupDetailsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.details',
                    excludeFromNav: true,
                    title: 'pages.titles.site.configuration.assetFamilyGroupDetails',
                  },
                },
                {
                  path: 'notifications',
                  name: 'app-site-configuration-notifications',
                  component: () => import('@/views/app/site/configuration/NotificationsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.notifications',
                    title: 'pages.titles.site.configuration.notifications',
                  },
                },
                {
                  path: 'reports',
                  name: 'app-site-configuration-reports',
                  component: () => import('@/views/app/site/configuration/ReportsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.reports',
                    title: 'pages.titles.site.configuration.reports',
                  },
                },
                {
                  path: 'alert-definitions',
                  name: 'app-site-configuration-alert-definitions',
                  component: () => import('@/views/app/site/configuration/AlertDefinitionsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.alerts',
                    title: 'pages.titles.site.configuration.alerts',
                  },
                },
                {
                  path: 'dsm-settings',
                  name: 'app-site-configuration-dsm-settings',
                  component: () => import('@/views/app/site/configuration/DsmSettingsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.dsmSettings',
                    title: 'pages.titles.site.configuration.dsmSettings',
                  },
                },
                {
                  path: 'settings',
                  name: 'app-site-configuration-settings',
                  component: () => import('@/views/app/site/configuration/SettingsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.settings',
                    title: 'pages.titles.site.configuration.settings',
                  },
                },
              ],
            },
            {
              path: 'fuel-logs',
              name: 'app-site-fuel-logs',
              component: () => import('@/views/app/site/FuelLogsView.vue'),
              meta: {
                title: 'pages.titles.site.fuelLogs',
              },
            },
            {
              path: 'technician-tools',
              name: 'app-site-technician-tools',
              component: () => import('@/views/app/site/TechnicianToolsView.vue'),
              meta: {
                authorize: authorizeFactory().getSiteMonitorReadingsUser(),
                title: 'pages.titles.site.technicianTools',
              },
            },
          ],
        },
        // '/asset-family-group'
        {
          path: '/resource-group/:assetFamilyGroupId',
          name: 'app-asset-family-group',
          components: {
            default: () => import('@/views/app/asset-family-group/AssetFamilyGroupView.vue'),
            Header: () => import('@/components/asset-family-group/AssetFamilyGroupHeader.vue'),
            Nav: () => import('@/components/asset-family-group/AssetFamilyGroupNav.vue'),
            Aside: () => import('@/components/asset-family-group/AssetFamilyGroupAside.vue'),
          },
          meta: {
            asideCollapsable: true,
            breadcrumb: { shouldClear: false },
          },
          props: true,
          redirect: {
            name: 'app-asset-family-group-overview',
          },
          children: [
            {
              path: 'overview',
              name: 'app-asset-family-group-overview',
              component: () => import('@/views/app/asset-family-group/OverviewView.vue'),
              meta: {
                title: 'pages.titles.assetFamilyGroup.overview',
              },
            },
          ],
        },
        // '/battery-bank'
        {
          path: '/battery-bank/:batteryBankType/:batteryBankId',
          name: 'app-battery-bank',
          components: {
            default: () => import('@/views/app/battery-bank/BatteryBankView.vue'),
            Header: () => import('@/components/battery-bank/BatteryBankHeader.vue'),
            Nav: () => import('@/components/battery-bank/BatteryBankNav.vue'),
            Aside: () => import('@/components/battery-bank/BatteryBankAside.vue'),
          },
          meta: {
            asideCollapsable: true,
            breadcrumb: { shouldClear: false },
          },
          props: true,
          redirect: {
            name: 'app-battery-bank-overview',
          },
          children: [
            {
              path: 'overview',
              name: 'app-battery-bank-overview',
              component: () => import('@/views/app/battery-bank/OverviewView.vue'),
              meta: {
                title: 'pages.titles.batteryBank.overview',
              },
            },
            {
              path: 'alerts',
              name: 'app-battery-bank-alerts',
              component: () => import('@/views/app/battery-bank/AlertsView.vue'),
              meta: {
                title: 'pages.titles.batteryBank.alerts',
              },
            },
            {
              path: 'configuration',
              name: 'app-battery-bank-configuration',
              component: () => import('@/views/app/battery-bank/ConfigurationView.vue'),
              redirect: {
                name: 'app-battery-bank-configuration-details',
              },
              children: [
                {
                  path: 'details',
                  name: 'app-battery-bank-configuration-details',
                  component: () => import('@/views/app/battery-bank/configuration/DetailsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.details',
                    title: 'pages.titles.batteryBank.configuration.details',
                  },
                },
                {
                  path: 'monitors',
                  name: 'app-battery-bank-configuration-monitors',
                  component: () => import('@/views/app/battery-bank/configuration/MonitorsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.monitors',
                    title: 'pages.titles.batteryBank.configuration.monitors',
                  },
                },
                {
                  path: 'alert-definitions',
                  name: 'app-battery-bank-configuration-alert-definitions',
                  component: () => import('@/views/app/battery-bank/configuration/AlertDefinitionsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.alerts',
                    title: 'pages.titles.batteryBank.configuration.alerts',
                  },
                },
                {
                  path: 'settings',
                  name: 'app-battery-bank-configuration-settings',
                  component: () => import('@/views/app/battery-bank/configuration/SettingsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.settings',
                    title: 'pages.titles.batteryBank.configuration.settings',
                  },
                },
              ],
            },
          ],
        },
        // '/generator'
        {
          path: '/generator/:generatorId',
          name: 'app-generator',
          components: {
            default: () => import('@/views/app/generator/GeneratorView.vue'),
            Header: () => import('@/components/generator/GeneratorHeader.vue'),
            Nav: () => import('@/components/generator/GeneratorNav.vue'),
            Aside: () => import('@/components/generator/GeneratorAside.vue'),
          },
          meta: {
            asideCollapsable: true,
            breadcrumb: { shouldClear: false },
          },
          props: true,
          redirect: {
            name: 'app-generator-overview',
          },
          children: [
            {
              path: 'overview',
              name: 'app-generator-overview',
              component: () => import('@/views/app/generator/OverviewView.vue'),
              meta: {
                title: 'pages.titles.generator.overview',
              },
            },
            {
              path: 'configuration',
              name: 'app-generator-configuration',
              component: () => import('@/views/app/generator/ConfigurationView.vue'),
              redirect: {
                name: 'app-generator-configuration-details',
              },
              children: [
                {
                  path: 'details',
                  name: 'app-generator-configuration-details',
                  component: () => import('@/views/app/generator/configuration/DetailsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.details',
                    title: 'pages.titles.generator.configuration.details',
                  },
                },
                {
                  path: 'monitors',
                  name: 'app-generator-configuration-monitors',
                  component: () => import('@/views/app/generator/configuration/MonitorsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.monitors',
                    title: 'pages.titles.generator.configuration.monitors',
                  },
                },
                {
                  path: 'alert-definitions',
                  name: 'app-generator-configuration-alert-definitions',
                  component: () => import('@/views/app/generator/configuration/AlertDefinitionsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.alerts',
                    title: 'pages.titles.generator.configuration.alerts',
                  },
                },
                {
                  path: 'settings',
                  name: 'app-generator-configuration-settings',
                  component: () => import('@/views/app/generator/configuration/SettingsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.settings',
                    title: 'pages.titles.generator.configuration.settings',
                  },
                },
              ],
            },
          ],
        },
        // '/power-grid'
        {
          path: '/power-grid/:powerGridId',
          name: 'app-power-grid',
          components: {
            default: () => import('@/views/app/power-grid/PowerGridView.vue'),
            Header: () => import('@/components/power-grid/PowerGridHeader.vue'),
            Nav: () => import('@/components/power-grid/PowerGridNav.vue'),
            Aside: () => import('@/components/power-grid/PowerGridAside.vue'),
          },
          meta: {
            asideCollapsable: true,
            breadcrumb: { shouldClear: false },
          },
          props: true,
          redirect: {
            name: 'app-power-grid-overview',
          },
          children: [
            {
              path: 'overview',
              name: 'app-power-grid-overview',
              component: () => import('@/views/app/power-grid/OverviewView.vue'),
              meta: {
                title: 'pages.titles.powerGrid.overview',
              },
            },
            {
              path: 'configuration',
              name: 'app-power-grid-configuration',
              component: () => import('@/views/app/power-grid/ConfigurationView.vue'),
              redirect: {
                name: 'app-power-grid-configuration-details',
              },
              children: [
                {
                  path: 'details',
                  name: 'app-power-grid-configuration-details',
                  component: () => import('@/views/app/power-grid/configuration/DetailsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.details',
                    title: 'pages.titles.powerGrid.configuration.details',
                  },
                },
                {
                  path: 'monitors',
                  name: 'app-power-grid-configuration-monitors',
                  component: () => import('@/views/app/power-grid/configuration/MonitorsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.monitors',
                    title: 'pages.titles.powerGrid.configuration.monitors',
                  },
                },
                {
                  path: 'alert-definitions',
                  name: 'app-power-grid-configuration-alert-definitions',
                  component: () => import('@/views/app/power-grid/configuration/AlertDefinitionsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.alerts',
                    title: 'pages.titles.powerGrid.configuration.alerts',
                  },
                },
                {
                  path: 'settings',
                  name: 'app-power-grid-configuration-settings',
                  component: () => import('@/views/app/power-grid/configuration/SettingsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.settings',
                    title: 'pages.titles.powerGrid.configuration.settings',
                  },
                },
              ],
            },
          ],
        },
        // '/solar-pv'
        {
          path: '/solar-pv/:solarPvId',
          name: 'app-solar-pv',
          components: {
            default: () => import('@/views/app/solar-pv/SolarPvView.vue'),
            Header: () => import('@/components/solar-pv/SolarPvHeader.vue'),
            Nav: () => import('@/components/solar-pv/SolarPvNav.vue'),
            Aside: () => import('@/components/solar-pv/SolarPvAside.vue'),
          },
          meta: {
            asideCollapsable: true,
            breadcrumb: { shouldClear: false },
          },
          props: true,
          redirect: {
            name: 'app-solar-pv-overview',
          },
          children: [
            {
              path: 'overview',
              name: 'app-solar-pv-overview',
              component: () => import('@/views/app/solar-pv/OverviewView.vue'),
              meta: {
                title: 'pages.titles.solarPv.overview',
              },
            },
            {
              path: 'configuration',
              name: 'app-solar-pv-configuration',
              component: () => import('@/views/app/solar-pv/ConfigurationView.vue'),
              redirect: {
                name: 'app-solar-pv-configuration-details',
              },
              children: [
                {
                  path: 'details',
                  name: 'app-solar-pv-configuration-details',
                  component: () => import('@/views/app/solar-pv/configuration/DetailsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.details',
                    title: 'pages.titles.solarPv.configuration.details',
                  },
                },
                {
                  path: 'monitors',
                  name: 'app-solar-pv-configuration-monitors',
                  component: () => import('@/views/app/solar-pv/configuration/MonitorsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.monitors',
                    title: 'pages.titles.solarPv.configuration.monitors',
                  },
                },
                {
                  path: 'alert-definitions',
                  name: 'app-solar-pv-configuration-alert-definitions',
                  component: () => import('@/views/app/solar-pv/configuration/AlertDefinitionsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.alerts',
                    title: 'pages.titles.solarPv.configuration.alerts',
                  },
                },
              ],
            },
          ],
        },
        // '/consumer'
        {
          path: '/consumer/:energyConsumerId',
          name: 'app-energy-consumer',
          components: {
            default: () => import('@/views/app/energy-consumer/EnergyConsumerView.vue'),
            Header: () => import('@/components/energy-consumer/EnergyConsumerHeader.vue'),
            Nav: () => import('@/components/energy-consumer/EnergyConsumerNav.vue'),
            Aside: () => import('@/components/energy-consumer/EnergyConsumerAside.vue'),
          },
          meta: {
            asideCollapsable: true,
            breadcrumb: { shouldClear: false },
          },
          props: true,
          redirect: {
            name: 'app-energy-consumer-overview',
          },
          children: [
            {
              path: 'overview',
              name: 'app-energy-consumer-overview',
              component: () => import('@/views/app/energy-consumer/OverviewView.vue'),
              meta: {
                title: 'pages.titles.energyConsumer.overview',
              },
            },
            {
              path: 'alerts',
              name: 'app-energy-consumer-alerts',
              component: () => import('@/views/app/energy-consumer/AlertsView.vue'),
              meta: {
                title: 'pages.titles.energyConsumer.alerts',
              },
            },
            {
              path: 'configuration',
              name: 'app-energy-consumer-configuration',
              component: () => import('@/views/app/energy-consumer/ConfigurationView.vue'),
              redirect: {
                name: 'app-energy-consumer-configuration-details',
              },
              children: [
                {
                  path: 'details',
                  name: 'app-energy-consumer-configuration-details',
                  component: () => import('@/views/app/energy-consumer/configuration/DetailsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.details',
                    title: 'pages.titles.energyConsumer.configuration.details',
                  },
                },
                {
                  path: 'monitors',
                  name: 'app-energy-consumer-configuration-monitors',
                  component: () => import('@/views/app/energy-consumer/configuration/MonitorsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.monitors',
                    title: 'pages.titles.energyConsumer.configuration.monitors',
                  },
                },
                {
                  path: 'alert-definitions',
                  name: 'app-energy-consumer-configuration-alert-definitions',
                  component: () => import('@/views/app/energy-consumer/configuration/AlertDefinitionsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.alerts',
                    title: 'pages.titles.energyConsumer.configuration.alerts',
                  },
                },
                {
                  path: 'settings',
                  name: 'app-energy-consumer-configuration-settings',
                  component: () => import('@/views/app/energy-consumer/configuration/SettingsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.settings',
                    title: 'pages.titles.energyConsumer.configuration.settings',
                  },
                },
                {
                  path: 'message-recipients',
                  name: 'app-energy-consumer-configuration-message-recipients',
                  component: () => import('@/views/app/energy-consumer/configuration/MessageRecipientsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.messageRecipients',
                    title: 'pages.titles.energyConsumer.configuration.messageRecipients',
                  },
                },
              ],
            },
          ],
        },
        // '/biogas'
        {
          path: '/biogas/:biogasId',
          name: 'app-biogas',
          components: {
            default: () => import('@/views/app/biogas/BiogasView.vue'),
            Header: () => import('@/components/biogas/BiogasHeader.vue'),
            Nav: () => import('@/components/biogas/BiogasNav.vue'),
            Aside: () => import('@/components/biogas/BiogasAside.vue'),
          },
          meta: {
            asideCollapsable: true,
            breadcrumb: { shouldClear: false },
          },
          props: true,
          redirect: {
            name: 'app-biogas-overview',
          },
          children: [
            {
              path: 'overview',
              name: 'app-biogas-overview',
              component: () => import('@/views/app/biogas/OverviewView.vue'),
              meta: {
                title: 'pages.titles.biogas.overview',
              },
            },
            {
              path: 'configuration',
              name: 'app-biogas-configuration',
              component: () => import('@/views/app/biogas/ConfigurationView.vue'),
              redirect: {
                name: 'app-biogas-configuration-details',
              },
              children: [
                {
                  path: 'details',
                  name: 'app-biogas-configuration-details',
                  component: () => import('@/views/app/biogas/configuration/DetailsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.details',
                    title: 'pages.titles.biogas.configuration.details',
                  },
                },
                {
                  path: 'monitors',
                  name: 'app-biogas-configuration-monitors',
                  component: () => import('@/views/app/biogas/configuration/MonitorsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.monitors',
                    title: 'pages.titles.biogas.configuration.monitors',
                  },
                },
                {
                  path: 'alert-definitions',
                  name: 'app-biogas-configuration-alert-definitions',
                  component: () => import('@/views/app/biogas/configuration/AlertDefinitionsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.alerts',
                    title: 'pages.titles.biogas.configuration.alerts',
                  },
                },
              ],
            },
          ],
        },
        // '/hydro'
        {
          path: '/hydro/:hydroId',
          name: 'app-hydro',
          components: {
            default: () => import('@/views/app/hydro/HydroView.vue'),
            Header: () => import('@/components/hydro/HydroHeader.vue'),
            Nav: () => import('@/components/hydro/HydroNav.vue'),
            Aside: () => import('@/components/hydro/HydroAside.vue'),
          },
          meta: {
            asideCollapsable: true,
            breadcrumb: { shouldClear: false },
          },
          props: true,
          redirect: {
            name: 'app-hydro-overview',
          },
          children: [
            {
              path: 'overview',
              name: 'app-hydro-overview',
              component: () => import('@/views/app/hydro/OverviewView.vue'),
              meta: {
                title: 'pages.titles.hydro.overview',
              },
            },
            {
              path: 'configuration',
              name: 'app-hydro-configuration',
              component: () => import('@/views/app/hydro/ConfigurationView.vue'),
              redirect: {
                name: 'app-hydro-configuration-details',
              },
              children: [
                {
                  path: 'details',
                  name: 'app-hydro-configuration-details',
                  component: () => import('@/views/app/hydro/configuration/DetailsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.details',
                    title: 'pages.titles.hydro.configuration.details',
                  },
                },
                {
                  path: 'monitors',
                  name: 'app-hydro-configuration-monitors',
                  component: () => import('@/views/app/hydro/configuration/MonitorsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.monitors',
                    title: 'pages.titles.hydro.configuration.monitors',
                  },
                },
                {
                  path: 'alert-definitions',
                  name: 'app-hydro-configuration-alert-definitions',
                  component: () => import('@/views/app/hydro/configuration/AlertDefinitionsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.alerts',
                    title: 'pages.titles.hydro.configuration.alerts',
                  },
                },
              ],
            },
          ],
        },
        // '/wind'
        {
          path: '/wind/:windId',
          name: 'app-wind',
          components: {
            default: () => import('@/views/app/wind/WindView.vue'),
            Header: () => import('@/components/wind/WindHeader.vue'),
            Nav: () => import('@/components/wind/WindNav.vue'),
            Aside: () => import('@/components/wind/WindAside.vue'),
          },
          meta: {
            asideCollapsable: true,
            breadcrumb: { shouldClear: false },
          },
          props: true,
          redirect: {
            name: 'app-wind-overview',
          },
          children: [
            {
              path: 'overview',
              name: 'app-wind-overview',
              component: () => import('@/views/app/wind/OverviewView.vue'),
              meta: {
                title: 'pages.titles.wind.overview',
              },
            },
            {
              path: 'configuration',
              name: 'app-wind-configuration',
              component: () => import('@/views/app/wind/ConfigurationView.vue'),
              redirect: {
                name: 'app-wind-configuration-details',
              },
              children: [
                {
                  path: 'details',
                  name: 'app-wind-configuration-details',
                  component: () => import('@/views/app/wind/configuration/DetailsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.details',
                    title: 'pages.titles.wind.configuration.details',
                  },
                },
                {
                  path: 'monitors',
                  name: 'app-wind-configuration-monitors',
                  component: () => import('@/views/app/wind/configuration/MonitorsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.monitors',
                    title: 'pages.titles.wind.configuration.monitors',
                  },
                },
                {
                  path: 'alert-definitions',
                  name: 'app-wind-configuration-alert-definitions',
                  component: () => import('@/views/app/wind/configuration/AlertDefinitionsView.vue'),
                  meta: {
                    buttonLabel: 'buttons.alerts',
                    title: 'pages.titles.wind.configuration.alerts',
                  },
                },
              ],
            },
          ],
        },
      ],
    },
    // /admin
    {
      path: '/admin',
      name: 'admin',
      component: () => import('@/layouts/AdminLayout.vue'),
      redirect: {
        name: 'admin-dashboard',
      },
      meta: {
        authorize: authorizeFactory().getSysAdmin(),
        setup: {
          user: true,
        },
      },
      children: [
        {
          path: 'partners',
          name: 'admin-dashboard',
          component: () => import('@/views/admin/PartnersView.vue'),
          meta: {
            title: 'pages.titles.admin.partners',
          },
        },
        {
          path: 'partners/:partnerId/organisations',
          name: 'admin-dashboard-partner-organisations',
          component: () => import('@/components/partner/PartnerOrganisations.vue'),
          meta: {
            title: 'pages.titles.admin.partnersOrganisations',
          },
        },
        {
          path: 'users',
          name: 'admin-users',
          component: () => import('@/views/admin/UsersView.vue'),
          meta: {
            title: 'pages.titles.admin.users',
          },
        },
        {
          path: 'location-fuel-costs',
          name: 'admin-location-fuel-costs',
          component: () => import('@/views/admin/LocationFuelCostsView.vue'),
          meta: {
            title: 'pages.titles.admin.locationFuelCosts',
          },
        },
      ],
    },
    // /fuel-data-contributor-dashboard
    {
      path: '/fuel-data-contributor-dashboard',
      name: 'fuel-data-contributor-dashboard',
      component: () => import('@/layouts/FuelDataContributorLayout.vue'),
      redirect: {
        name: 'fuel-data-contributor-dashboard-overview',
      },
      meta: {
        authorize: authorizeFactory().getFuelDataContributor(),
        setup: {
          user: true,
        },
      },
      children: [
        {
          path: 'overview',
          name: 'fuel-data-contributor-dashboard-overview',
          component: () => import('@/views/app/fuel-data-contributor/OverviewView.vue'),
          meta: {
            title: 'pages.titles.fuelDataContributorDashboard.overview',
          },
        },
        {
          path: 'logs',
          name: 'fuel-data-contributor-dashboard-logs',
          component: () => import('@/views/app/fuel-data-contributor/LogsView.vue'),
          meta: {
            title: 'pages.titles.fuelDataContributorDashboard.logs',
          },
        },
      ],
    },
    // /summary
    {
      path: '/summary',
      name: 'summary',
      component: () => import('@/layouts/SummaryLayout.vue'),
      redirect: {
        name: 'summary-dashboard',
      },
      meta: {
        authorize: authorizeFactory().getSiteUser(),
        setup: {
          user: true,
        },
      },
      children: [
        {
          path: 'dashboard',
          name: 'summary-dashboard',
          component: () => import('@/views/app/summary/DashboardView.vue'),
          meta: {
            title: 'pages.titles.summary.dashboard',
          },
        },
        {
          path: 'usage',
          name: 'summary-usage',
          component: () => import('@/views/app/summary/UsageView.vue'),
          meta: {
            title: 'pages.titles.summary.usage',
          },
        },
        {
          path: 'fuel-data',
          name: 'summary-fuel-data',
          component: () => import('@/views/app/summary/FuelDataView.vue'),
          meta: {
            title: 'pages.titles.site.fuelLogs',
          },
        },
      ],
    },
    // /technician
    {
      path: '/technician',
      name: 'technician',
      component: () => import('@/layouts/TechnicianLayout.vue'),
      redirect: {
        name: 'technician-dashboard',
      },
      meta: {
        authorize: authorizeFactory().getPartnerTechnician(),
        setup: {
          user: true,
        },
      },
      children: [
        {
          path: 'dashboard',
          name: 'technician-dashboard',
          component: () => import('@/views/app/technician/DashboardView.vue'),
          meta: {
            title: 'pages.titles.technician.dashboard',
          },
        },
      ],
    },
    // /site/:siteId/system-editor (full screen, outside AppLayout; the name must not start with `app-` so partner
    // technicians aren't redirected away)
    {
      path: '/site/:siteId/system-editor',
      component: () => import('@/layouts/EditorLayout.vue'),
      meta: {
        authorize: authorizeFactory().getOrganisationAdmin(),
        requiresActiveSite: true,
        setup: {
          user: true,
        },
      },
      beforeEnter: (to) => {
        const userStore = useUserStore()

        if (!userStore.systemEditorEnabled || !userStore.hasWritePermission) {
          return { name: 'app-site-overview', params: { siteId: to.params.siteId } }
        }

        return true
      },
      children: [
        {
          path: '',
          name: 'system-editor',
          component: () => import('@/views/system-editor/SystemEditorView.vue'),
          meta: {
            title: 'systemEditor.title',
          },
        },
      ],
    },
    // /auth
    {
      path: '/auth',
      name: 'auth',
      redirect: {
        name: 'auth-login',
      },
      beforeEnter: () => {
        const userStore = useUserStore()

        if (userStore.isLoggedIn()) {
          return { name: 'app' }
        }

        return true
      },
      component: () => import('@/layouts/PublicLayout.vue'),
      children: [
        {
          path: 'login',
          name: 'auth-login',
          component: () => import('@/views/auth/LoginView.vue'),
          meta: {
            title: 'pages.titles.login',
          },
        },
        {
          path: 'forgot-password',
          name: 'auth-forgot-password',
          component: () => import('@/views/auth/ForgotPasswordView.vue'),
          meta: {
            title: 'pages.titles.forgotPassword',
          },
        },
        {
          path: 'reset-password',
          name: 'auth-reset-password',
          component: () => import('@/views/auth/ResetPasswordView.vue'),
          meta: {
            title: 'pages.titles.resetPassword',
          },
        },
        {
          path: 'change-password',
          name: 'auth-change-password',
          component: () => import('@/views/auth/ChangePasswordView.vue'),
          meta: {
            title: 'pages.titles.changePassword',
          },
        },
      ],
    },
    {
      path: '/_playground',
      name: 'playground',
      component: () => import('@/views/playground/PlaygroundView.vue'),
      meta: {
        setup: {
          user: true,
        },
        title: 'Playground',
      },
    },
    {
      path: '/:pathMatch(.*)*/:reason?',
      name: 'not-found',
      component: () => import('@/views/app/PageNotFoundView.vue'),
      meta: {
        title: 'pages.titles.notFound',
      },
    },
  ],
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    else if (to.hash) {
      return {
        behavior: 'smooth',
        el: to.hash,
      }
    }
    else {
      return { top: 0 }
    }
  },
})

router.beforeEach(async (to, from, next) => {
  if (to.name) {
    // Start the route progress bar.
    NProgress.start()
  }

  const userStore = useUserStore()

  if (!to?.name?.toString().startsWith('auth-') && !userStore.isLoggedIn()) {
    next({ name: 'auth-login', query: { redirect: to.fullPath } })
  }
  else {
    let hasNextBeenCalled = false

    const nextWrapped: NavigationGuardNext = (...args: []) => {
      if (hasNextBeenCalled === false) {
        next(...args)
      }

      hasNextBeenCalled = true
    }

    // This will setup the user and organisation IF the route requests it.
    if (setupHook.beforeEachAuthenticated) {
      await setupHook.beforeEachAuthenticated(to, from, nextWrapped)
    }

    // This prevents access to areas that require additional checks. It will call the "next" call back if the access control fails.
    if (authorizeHook.beforeEachAuthenticated) {
      await authorizeHook.beforeEachAuthenticated(to, from, nextWrapped)
    }

    // Validates resources (e.g., active site) via meta flags
    if (validateResourceHook.beforeEachAuthenticated) {
      await validateResourceHook.beforeEachAuthenticated(to, from, nextWrapped)
    }

    // This provides routes with the ability to control the breadcrumb e.g. clear
    if (breadcrumbHook.beforeEachAuthenticated) {
      await breadcrumbHook.beforeEachAuthenticated(to, from, nextWrapped)
    }

    // This provides routes with the ability to control whether the aside is collapsable.
    if (asideCollapsibleHook.beforeEachAuthenticated) {
      await asideCollapsibleHook.beforeEachAuthenticated(to, from, nextWrapped)
    }

    // Continue navigation if we pass all of the above.
    if (hasNextBeenCalled === false) {
      next()
    }
  }
})

router.afterEach((to, from, failure) => {
  // Complete the animation of the route progress bar.
  NProgress.done()

  if (failure) {
    console.error('[router failure]', to, from, failure)
  }
})

router.onError((error, to) => {
  // These errors can occur after a deployment.
  if (
    error.message.includes('Failed to fetch dynamically imported module')
    || error.message.includes('Importing a module script failed')
  ) {
    if (!to?.fullPath) {
      window.location.reload()
    }
    else {
      window.location.href = to.fullPath
    }
  }
})

export default router
